import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Info } from 'lucide-react';

/**
 * A small "i" button that holds an explanation, so pages can keep only the essential text on screen.
 * Opens on hover, on keyboard focus and on tap/click; Escape, blur, a tap elsewhere or leaving closes it.
 * The bubble stays in the DOM (hidden) and describes the button (aria-describedby), so screen readers
 * read it with the button's label. Positioned against the viewport (fixed), clamped to its edges.
 *
 *   <InfoTip label="Sobre o CTR">Cliques ÷ impressões.</InfoTip>
 */
export function InfoTip({ label, children, size = 14 }: { label: string; children: ReactNode; size?: number }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const wrap = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const bubble = useRef<HTMLSpanElement>(null);
  const closeTimer = useRef<number | null>(null);
  /** When hover/focus opened it: a tap fires mouseenter, focus and click in a row, and must not close it again. */
  const openedAt = useRef(0);

  const cancelClose = () => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const show = useCallback(() => {
    cancelClose();
    setOpen((was) => {
      if (!was) openedAt.current = Date.now();
      return true;
    });
  }, []);
  const hideSoon = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  };

  useLayoutEffect(() => {
    if (!open || !button.current || !bubble.current) return;
    const b = button.current.getBoundingClientRect();
    const w = bubble.current.offsetWidth;
    const h = bubble.current.offsetHeight;
    const vw = document.documentElement.clientWidth;
    const left = Math.max(8, Math.min(b.left + b.width / 2 - w / 2, vw - w - 8));
    const below = b.bottom + 6;
    const top = below + h > window.innerHeight - 8 && b.top - 6 - h > 8 ? b.top - 6 - h : below;
    setPos({ top, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e: PointerEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  useEffect(() => cancelClose, []);

  return (
    <span ref={wrap} className="infotip" onMouseEnter={show} onMouseLeave={hideSoon}>
      <button
        ref={button}
        type="button"
        className="infotip__btn"
        aria-label={label}
        aria-describedby={id}
        aria-expanded={open}
        onFocus={show}
        onBlur={hideSoon}
        onClick={() => (open && Date.now() - openedAt.current > 400 ? setOpen(false) : show())}
      >
        <Info size={size} aria-hidden="true" />
      </button>
      <span
        ref={bubble}
        id={id}
        role="tooltip"
        className="infotip__bubble"
        hidden={!open}
        style={open && pos ? { top: pos.top, left: pos.left } : open ? { visibility: 'hidden' } : undefined}
      >
        {children}
      </span>
    </span>
  );
}
